import { CircleHostCard } from '@modernhealth/circles-web-components';
import { BodyTextMedium, HeadingText40, HeadingText50 } from '@modernhealth/vital-components';
import React from 'react';
import { useIntl } from 'react-intl';
import { useHistory, useParams } from 'react-router-dom';
import { CircleTopicBaseFragment } from '@modernhealth/circles-graphql/v2';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useTranslation } from 'react-i18next';
import { useAnalytics } from '@modernhealth/web-analytics';

import { Grid } from 'src/shared/components/Grid';

import { Page } from '../components/PageLayout';
import { UnorderedList } from '../components/UnorderedList';
import { BackButton } from '../components/BackButton';
import { CircleOnDemandFauxVideoPlayer } from '../components/CircleOnDemandFauxVideoPlayer';
import { CircleTopicCard } from '../components/CircleTopicCard';
import { CircleNotFound } from '../components/CircleNotFound';
import { useViewedOnDemandTopicMutation } from '../documents/circleUserViewedOnDemandTopic.generated';
import { useGetOnDemandCircleBySlugQuery } from '../documents/getOnDemandCircleBySlug.generated';

const maximumPlayerWidth = 560;

export const CirclesOnDemandCirclePage = () => {
    const intl = useIntl();
    const analytics = useAnalytics();
    const { t } = useTranslation('circles');
    const history = useHistory();
    const { topicSlug = '' } = useParams<{ topicSlug?: string }>();
    const { circlesShowOnDemandFeedback } = useFlags();
    const { data, loading } = useGetOnDemandCircleBySlugQuery({
        variables: { slug: topicSlug },
    });
    const [viewedOnDemandTopic] = useViewedOnDemandTopicMutation({
        errorPolicy: 'ignore',
    });

    if (loading) return null;

    const ondemand = data?.circleOnDemandBySlug;

    if (ondemand?.__typename !== 'CircleOnDemandNode') return <CircleNotFound />;

    const { video } = ondemand;

    const trackClick = () => {
        analytics.trackClick('On Demand link clicked', {
            page_name: 'circles-on-demand',
            on_demand_topic_slug: topicSlug,
            on_demand_topic_uuid: ondemand.uuid,
            destination: video.externalVideoUrl,
            on_demand_topic_language: ondemand.languageCode,
        });

        viewedOnDemandTopic({
            variables: {
                onDemandTopicId: ondemand.id,
            },
        });
    };

    return (
        <Page.Container>
            <Page.Header>
                <BackButton />
            </Page.Header>
            <Page.Content
                centered
                alignChildren="start"
                css={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 40,
                    maxWidth: maximumPlayerWidth,
                }}
            >
                <CircleOnDemandFauxVideoPlayer
                    name={ondemand.name}
                    languageCode={ondemand.languageCode}
                    seconds={video.durationInSeconds}
                    thumb={video.thumbnail}
                    externalLink={video.externalVideoUrl}
                    timeout={1000}
                    onClick={() => {
                        trackClick();

                        if (circlesShowOnDemandFeedback) {
                            history.push(`/circles/on-demand/${topicSlug}/feedback`);
                        }
                    }}
                />
                <section>
                    <HeadingText50>{ondemand.name}</HeadingText50>
                    <BodyTextMedium>{ondemand.description}</BodyTextMedium>
                </section>
                <section>
                    <HeadingText40 css={{ marginBottom: 24 }}>
                        {t('pages.on_demand.hosts')}
                    </HeadingText40>
                    <div
                        css={{
                            display: 'grid',
                            gap: 16,
                            gridTemplateColumns: 'repeat(auto-fit, minmax(200px,1fr))',
                        }}
                    >
                        {ondemand.facilitators.map(facilitator => {
                            return (
                                <CircleHostCard
                                    key={facilitator.id}
                                    name={facilitator.name}
                                    photo={facilitator.photo}
                                    credential={facilitator.credential}
                                    country={
                                        facilitator.countryCode
                                            ? intl.formatDisplayName(facilitator.countryCode, {
                                                  type: 'region',
                                              })
                                            : undefined
                                    }
                                />
                            );
                        })}
                    </div>
                </section>
                {ondemand.relatedTopics && ondemand.relatedTopics.length > 0 && (
                    <section>
                        <HeadingText40 css={{ marginBottom: 24 }}>
                            {t(['pages.on_demand.related_topics.title', ''], {
                                fallbackLng: [''],
                            })}
                        </HeadingText40>
                        <Grid as={UnorderedList} minItemWidth={250} expand={false} maxColumns={2}>
                            {ondemand.relatedTopics.map(topic => {
                                return (
                                    <CircleTopicCard
                                        topic={topic as CircleTopicBaseFragment}
                                        id={`on-demand-related-${topic.id}`}
                                        key={topic.id}
                                    />
                                );
                            })}
                        </Grid>
                    </section>
                )}
            </Page.Content>
        </Page.Container>
    );
};
